import { useCallback, useMemo, useState } from 'react';
import { ProjectNavigationTarget } from '../views/projects/settings';
import { useProjectId } from 'store/project';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';

const urlQueryName = 'target';

function saveNavigationTargetInBrowser(
  projectId: string,
  target: ProjectNavigationTarget
): void {
  LocalStorageManager.setNavigationTarget(projectId, target);
  const url = new URL(window.location.href);
  url.searchParams.set(urlQueryName, target);

  window.history.pushState(null, '', url.toString());
}

function isProjectNavigationTarget(
  name: null | string | undefined
): name is ProjectNavigationTarget {
  if (!name) {
    return false;
  }
  return Object.values(ProjectNavigationTarget).includes(
    name as ProjectNavigationTarget
  );
}

function getNavigationTargetFromUrl():
  | ProjectNavigationTarget
  | undefined {
  const sourced = new URLSearchParams(window.location.search).get(
    urlQueryName
  );

  if (isProjectNavigationTarget(sourced)) {
    return sourced;
  }
}
function getNavigationTargetFromLocalStorage(
  projectId: string
): ProjectNavigationTarget | undefined {
  const result = LocalStorageManager.getNavigationTarget<ProjectNavigationTarget>(projectId);

  if (result && isProjectNavigationTarget(result)) {
    return result;
  }
  return undefined;
}

function determineNavigationTarget(
  projectId: string
): ProjectNavigationTarget {
  const fromUrl = getNavigationTargetFromUrl();
  if (fromUrl) {
    LocalStorageManager.setNavigationTarget(projectId, fromUrl);
    return fromUrl;
  }
  const fromStorage = getNavigationTargetFromLocalStorage(projectId);

  return fromStorage || ProjectNavigationTarget.generalProjectData;
}

type UseNavigationTargetHook = () => {
  setTarget: SaveNavigationTarget;
  target: ProjectNavigationTarget;
};

export type SaveNavigationTarget = (
  target: ProjectNavigationTarget
) => void;
export const useNavigationTarget: UseNavigationTargetHook = () => {
  const projectId = useProjectId();

  const [target, _setTarget] = useState<ProjectNavigationTarget>(
    determineNavigationTarget(projectId)
  );

  const saveNavigationTarget: SaveNavigationTarget = useCallback(
    (target: ProjectNavigationTarget): void => {
      _setTarget(target);
      saveNavigationTargetInBrowser(projectId, target);
    },
    [projectId]
  );

  return useMemo(
    () => ({
      target,
      setTarget: saveNavigationTarget,
    }),
    [target, saveNavigationTarget]
  );
};
